import {
  DbExplorerStateAndActions,
  useDbExplorerStore,
} from "../../stores/dbExplorerStore";
import { useShallow } from "zustand/react/shallow";

import { DataTableRenderer } from "../Common/DataTableRenderer";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../ui/pagination";
import { AlertCircle, Loader2 } from "lucide-react";
import React from "react";

export function DataTable() {
  const {
    selectedTable,
    tableData,
    loading,
    appliedFilters,
    currentPage,
    setCurrentPage,
    handleSort,
  } = useDbExplorerStore(
    useShallow((state: DbExplorerStateAndActions) => ({
      selectedTable: state.selectedTable,
      tableData: state.tableData,
      loading: state.loading,
      appliedFilters: state.appliedFilters,
      currentPage: state.currentPage,
      setCurrentPage: state.setCurrentPage,
      handleSort: state.handleSort,
    })),
  );

  const renderPaginationItems = () => {
    if (!tableData || !tableData.pagination) return null;
    const { totalPages } = tableData.pagination;
    const items = [];

    items.push(
      <PaginationItem key="first">
        <PaginationLink
          onClick={() => setCurrentPage(1)}
          className={currentPage <= 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
        >
          First
        </PaginationLink>
      </PaginationItem>,
    );

    items.push(
      <PaginationItem key="prev">
        <PaginationPrevious
          onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
          className={currentPage <= 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
        />
      </PaginationItem>,
    );

    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <PaginationItem key={i}>
          <PaginationLink
            onClick={() => setCurrentPage(i)}
            isActive={currentPage === i}
            className="cursor-pointer"
          >
            {i}
          </PaginationLink>
        </PaginationItem>,
      );
    }

    items.push(
      <PaginationItem key="next">
        <PaginationNext
          onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
          className={
            currentPage >= totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"
          }
        />
      </PaginationItem>,
    );

    items.push(
      <PaginationItem key="last">
        <PaginationLink
          onClick={() => setCurrentPage(totalPages)}
          className={
            currentPage >= totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"
          }
        >
          Last
        </PaginationLink>
      </PaginationItem>,
    );

    return items;
  };

  if (!selectedTable) return null;

  return (
    <div className="h-full max-w-full flex flex-col">
      {loading ? (
        <div className="flex-1 min-h-0 flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
            <div className="space-y-2">
              <p className="text-slate-700 font-semibold text-lg">Loading Data</p>
              <p className="text-slate-500 text-sm">Fetching table data...</p>
            </div>
          </div>
        </div>
      ) : tableData ? (
        <div className="flex flex-col h-full">
          <div className="flex-1 min-h-0">
            <DataTableRenderer
              data={tableData.data}
              columns={tableData.columns}
              sortColumn={appliedFilters.sortColumn}
              sortDirection={appliedFilters.sortDirection}
              onSort={handleSort}
            />
          </div>

          {tableData.pagination && (
            <div className="shrink-0 flex items-center justify-between px-8 py-2 border-t border-slate-200/60">
              <div className="text-xs text-slate-600 bg-slate-50/50 px-4 py-2 rounded-lg">
                Showing{" "}
                <span className="font-semibold text-slate-800">
                  {(tableData.pagination.currentPage - 1) * tableData.pagination.limit + 1}
                </span>{" "}
                -{" "}
                <span className="font-semibold text-slate-800">
                  {Math.min(
                    tableData.pagination.currentPage * tableData.pagination.limit,
                    tableData.pagination.totalRows,
                  )}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-slate-800">
                  {tableData.pagination.totalRows}
                </span>{" "}
                rows
              </div>
              <Pagination>
                <PaginationContent>{renderPaginationItems()}</PaginationContent>
              </Pagination>
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex items-center justify-center">
          <div className="space-y-4 p-8 rounded-md bg-white/60 border border-slate-200/60 shadow-sm max-w-md mx-auto">
            <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
              <AlertCircle className="h-8 w-8 text-slate-400" />
            </div>
            <div className="space-y-2">
              <p className="text-slate-600 font-semibold text-lg">No Data Available</p>
              <p className="text-slate-500 text-sm">
                No data available for this table or an error occurred.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
